import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from '../auth';
import { useToast } from '../use-toast';

export interface EmailTemplate {
  id: string;
  org_id: string;
  created_by: string;
  name: string;
  category: string;
  subject: string | null;
  body_html: string;
  variables: string[];
  usage_count: number;
  is_shared: boolean;
  created_at: string;
}

export type CreateTemplateInput = Omit<EmailTemplate, 'id' | 'org_id' | 'created_by' | 'created_at' | 'usage_count'>;

const DEFAULT_CATEGORIES = ['Prospecção', 'Follow-up', 'Proposta', 'Reunião', 'Onboarding', 'Suporte', 'Geral'];

function extractVariables(html: string): string[] {
  const matches = html.matchAll(/\{\{(\w+)\}\}/g);
  const vars = new Set<string>();
  for (const match of matches) {
    vars.add(match[1]);
  }
  return Array.from(vars);
}

export function useEmailTemplates() {
  const { orgId } = useCurrentOrgId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: templates = [], isLoading, error } = useQuery({
    queryKey: ['email_templates', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('org_id', orgId)
        .order('usage_count', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EmailTemplate[];
    },
    enabled: !!orgId,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      if (!orgId) throw new Error('Organização não encontrada');

      const variables = extractVariables(input.body_html);

      const { data, error } = await supabase
        .from('email_templates')
        .insert({
          org_id: orgId,
          created_by: user.id,
          name: input.name,
          category: input.category,
          subject: input.subject ?? null,
          body_html: input.body_html,
          variables,
          is_shared: input.is_shared,
        })
        .select()
        .single();

      if (error) throw error;
      return data as EmailTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email_templates', orgId] });
    },
    onError: () => {
      toast({ title: 'Erro ao criar template', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateTemplateInput> }) => {
      const updates: Record<string, unknown> = { ...data, updated_at: new Date().toISOString() };
      if (data.body_html) {
        updates.variables = extractVariables(data.body_html);
      }
      const { error } = await supabase
        .from('email_templates')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email_templates', orgId] });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar template', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email_templates', orgId] });
    },
    onError: () => {
      toast({ title: 'Erro ao excluir template', variant: 'destructive' });
    },
  });

  const incrementUsage = (id: string) => {
    // Fire-and-forget: optimistic local update + background DB update
    queryClient.setQueryData<EmailTemplate[]>(['email_templates', orgId], (prev) =>
      prev?.map((t) => t.id === id ? { ...t, usage_count: t.usage_count + 1 } : t) ?? []
    );
    supabase
      .from('email_templates')
      .update({ usage_count: (templates.find((t) => t.id === id)?.usage_count ?? 0) + 1 })
      .eq('id', id)
      .then(() => {});
  };

  const categories = useMemo(() => {
    const fromTemplates = templates.map((t) => t.category);
    const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...fromTemplates]));
    return merged;
  }, [templates]);

  return {
    templates,
    loading: isLoading,
    error: error ? String(error) : null,
    createTemplate: createMutation.mutateAsync,
    updateTemplate: (id: string, data: Partial<CreateTemplateInput>) =>
      updateMutation.mutateAsync({ id, data }),
    deleteTemplate: deleteMutation.mutateAsync,
    incrementUsage,
    categories,
    isCreating: createMutation.isPending,
  };
}
